import React, { useState, useEffect } from 'react';

const LifecycleTimeKPISettings = ({
  kpiTitle,
  onKpiTitleChange,
  objectType,
  onObjectTypeChange,
  fromActivity,
  onFromActivityChange,
  toActivity,
  onToActivityChange,
  aggregationMethod,
  onAggregationMethodChange,
  objectTypes,
  activities,
  loading = false,
  objectTypesLoading = false,
  activitiesLoading = false,
  onKeyPress,
  activeFile,
  orderingHint,
}) => {
  const [availableActivities, setAvailableActivities] = useState([]);

  // Update available activities when object type changes
  useEffect(() => {
    if (objectType && activities) {
      setAvailableActivities(activities);
    } else {
      setAvailableActivities([]);
    }
  }, [objectType, activities]);

  const showWarning = fromActivity && toActivity && fromActivity === toActivity;
  const activitiesReady = objectType && (availableActivities.length > 0 || !activitiesLoading);

  return (
    <div className="space-y-6">
      {/* Ordering hint when B commonly occurs before A */}
      {orderingHint && (
        <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
          <div className="flex items-start gap-2">
            <svg className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            <div className="text-sm text-amber-700">
              {orderingHint}
            </div>
          </div>
        </div>
      )}
      {/* KPI Title */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          KPI Title *
        </label>
        <input
          type="text"
          value={kpiTitle}
          onChange={(e) => onKpiTitleChange(e.target.value)}
          onKeyPress={onKeyPress}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder="e.g., Order to Delivery Time"
          disabled={loading}
        />
        <p className="text-xs text-gray-500 mt-1">
          Custom name for your KPI widget
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Object Type Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Object Type *
          </label>
          <select
            value={objectType}
            onChange={(e) => onObjectTypeChange(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            disabled={loading || objectTypesLoading}
          >
            <option value="">
              {objectTypesLoading ? "Loading..." : "Select Object Type..."}
            </option>
            {objectTypes.map((type) => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </select>
          <p className="text-xs text-gray-500 mt-1">
            Object type for lifecycle analysis
          </p>
        </div>

        {/* Aggregation Method */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Aggregation Method *
          </label>
          <select
            value={aggregationMethod}
            onChange={(e) => onAggregationMethodChange(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            disabled={loading}
          >
            <option value="median">Median</option>
            <option value="average">Average</option>
            <option value="minimum">Minimum</option>
            <option value="maximum">Maximum</option>
          </select>
          <p className="text-xs text-gray-500 mt-1">
            How to aggregate time differences
          </p>
        </div>

        {/* From Activity Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            From Activity (A) *
          </label>
          <select
            value={fromActivity}
            onChange={(e) => onFromActivityChange(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
            disabled={!objectType || loading || (activitiesLoading && availableActivities.length === 0)}
          >
            {!objectType && <option value="">Select object type first...</option>}
            {objectType && <option value="">Select activity...</option>}
            {objectType && !activitiesLoading && availableActivities.length === 0 && (
              <option value="">No activities available</option>
            )}
            {availableActivities.map((activity) => (
              <option key={activity} value={activity}>
                {activity}
              </option>
            ))}
          </select>
          <p className="text-xs text-gray-500 mt-1">
            Starting activity for time measurement
          </p>
        </div>

        {/* To Activity Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            To Activity (B) *
          </label>
          <select
            value={toActivity}
            onChange={(e) => onToActivityChange(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
            disabled={!objectType || loading || (activitiesLoading && availableActivities.length === 0)}
          >
            {!objectType && <option value="">Select object type first...</option>}
            {objectType && <option value="">Select activity...</option>}
            {objectType && !activitiesLoading && availableActivities.length === 0 && (
              <option value="">No activities available</option>
            )}
            {availableActivities.filter(activity => activity !== fromActivity).map((activity) => (
              <option key={activity} value={activity}>
                {activity}
              </option>
            ))}
          </select>
          <p className="text-xs text-gray-500 mt-1">
            Ending activity for time measurement
          </p>
        </div>
      </div>

      {/* Validation Messages */}
      {showWarning && (
        <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
          <div className="flex items-center">
            <svg className="w-5 h-5 text-amber-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            <span className="text-sm text-amber-700">
              From and To activities must be different
            </span>
          </div>
        </div>
      )}

      {/* Info about Activity B timing */}
      <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
        <div className="flex items-start gap-3">
          <svg className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
          </svg>
          <div className="flex-1">
            <h4 className="text-sm font-medium text-blue-800 mb-1">
              Activity Timing Logic
            </h4>
            <p className="text-sm text-blue-700">
              Activity B may occur after Activity A with other activities in between.
              The system finds the first occurrence of Activity B that happens after
              the first occurrence of Activity A within each object lifecycle.
            </p>
          </div>
        </div>
      </div>


    </div>
  );
};

export default LifecycleTimeKPISettings;
